import type { FC } from 'react';
import NumberFlow from '@number-flow/react';

import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import type { FinancialValueDisplayPreference } from '@/types/financialValueDisplay';

import { useFinancialValueDisplay } from './financialValueDisplayContextState';
import type { FinancialValueFormatOptions } from './formatFinancialValue';
import { resolveFinancialValuePresentation } from './formatFinancialValue';

export interface FinancialValueProps extends FinancialValueFormatOptions {
    preference?: FinancialValueDisplayPreference;
    value?: number | string | null;
}

export const FinancialValue: FC<FinancialValueProps> = ({
    currency,
    kind,
    preference: preferenceOverride,
    scalable,
    value,
}) => {
    const { preference: inheritedPreference } = useFinancialValueDisplay();
    const preference = preferenceOverride ?? inheritedPreference;
    const numericValue =
        typeof value === 'string' && value.trim() ? Number(value) : typeof value === 'string' ? null : value;

    if (numericValue === null || numericValue === undefined || !Number.isFinite(numericValue)) {
        return <>—</>;
    }

    const presentation = resolveFinancialValuePresentation(numericValue, preference, { currency, kind, scalable });
    const number = (
        <span
            aria-label={
                presentation.disclosureRequired
                    ? `${presentation.formatted}; exact value ${presentation.exact}`
                    : presentation.exact
            }
            data-exact-value={numericValue}
            style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
        >
            <span aria-hidden='true'>
                <NumberFlow
                    format={presentation.format}
                    locales='en-US'
                    prefix={presentation.prefix}
                    suffix={presentation.suffix}
                    value={presentation.scaledValue}
                    willChange
                />
            </span>
        </span>
    );

    return presentation.disclosureRequired ? (
        <SmallTooltip showInfo={false} title={`Exact value: ${presentation.exact}`}>
            {number}
        </SmallTooltip>
    ) : (
        number
    );
};
